import React, {
  createContext,
  forwardRef,
  useContext,
  useCallback,
  useMemo,
  useReducer,
} from 'react';
import { hasValue, isFunction } from 'swipeclock-frontend';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import MuiAlert from '@mui/material/Alert';
import MuiSnackbar from '@mui/material/Snackbar';
import PropTypes from 'prop-types';

export const SetSnackbarContext = createContext();

export function useSetSnackbarContext() {
  return useContext(SetSnackbarContext);
}

export const CloseSnackbarContext = createContext();

export function useCloseSnackbarContext() {
  return useContext(CloseSnackbarContext);
}

const initialState = {
  currentSnackbar: undefined,
  snackbars: [],
};

function reducer(state, action) {
  switch (action.type) {
    case 'set_snackbar':
      return {
        ...state,
        snackbars: [...state.snackbars, action.snackbar],
      };
    case 'remove_snackbar':
      return {
        ...state,
        snackbars: state.snackbars.filter((s) => s.key !== action.snackbar.key),
      };
    default:
      return state;
  }
}

export default function SnackbarProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { snackbars } = state ?? {};

  const setSnackbar = useCallback((snackbar) => {
    // Push snackbar to queue
    if (hasValue(snackbar) && hasValue(snackbar.key)) {
      return dispatch({
        type: 'set_snackbar',
        snackbar,
      });
    }
    return console.error(
      'Must provide an id for the snackbar!, snackbar = { key: "foo" }'
    );
  }, []);

  const currentSnackbar = useMemo(() => {
    if (hasValue(snackbars) && snackbars.length > 0) {
      const queue = [...snackbars];
      const snackbar = queue.shift(); // FIFO
      return snackbar;
    }
    return null;
  }, [snackbars]);

  const handleCloseSnackbar = useCallback(
    (snackbar) => () => {
      if (isFunction(snackbar?.onClose)) {
        snackbar?.onClose();
      }

      // Remove snackbar from store
      dispatch({
        type: 'remove_snackbar',
        snackbar,
      });
    },

    [currentSnackbar]
  );

  return (
    <SetSnackbarContext.Provider value={setSnackbar}>
      <CloseSnackbarContext.Provider
        value={handleCloseSnackbar(currentSnackbar)}
      >
        {children}
        {currentSnackbar && (
          <Snackbar
            onClose={handleCloseSnackbar(currentSnackbar)}
            snackbar={currentSnackbar}
          />
        )}
      </CloseSnackbarContext.Provider>
    </SetSnackbarContext.Provider>
  );
}

SnackbarProvider.propTypes = {
  children: PropTypes.any,
};

function Snackbar({ onClose: handleClose, snackbar }) {
  const { sx, content, message, key, action, severity } = snackbar ?? {
    sx: {},
  };

  const closeAction = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  const anchor = snackbar?.anchor
    ? snackbar.anchor
    : { vertical: 'bottom', horizontal: 'left' };

  const hide = snackbar.hide !== undefined ? snackbar.hide : 6000;

  return (
    <>
      {severity && (
        <MuiSnackbar
          onClose={handleClose}
          anchorOrigin={anchor}
          autoHideDuration={hide}
          key={key}
          open
        >
          <Alert
            action={action ?? closeAction}
            severity={severity}
            sx={{ width: '100%', ...sx }}
          >
            {hasValue(content) ? content : message}
          </Alert>
        </MuiSnackbar>
      )}
      {!severity && (
        <MuiSnackbar
          action={
            <>
              {action}
              {closeAction}
            </>
          }
          anchorOrigin={anchor}
          autoHideDuration={hide}
          key={key}
          message={message}
          onClose={handleClose}
          open
          sx={sx}
        />
      )}
    </>
  );
}

Snackbar.propTypes = {
  onClose: PropTypes.func,
  snackbar: PropTypes.object,
};

/* eslint-disable prefer-arrow-callback */
const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
